import React, { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Copy, EnvelopeSimple, WhatsappLogo, Info, Phone, GlobeSimple } from "@phosphor-icons/react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { eur, price } from "@/lib/format";
import { PageHeader } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import WhatsAppInbox from "@/components/WhatsAppInbox";
import { messageText } from "@/i18n/messageTranslations";
import { extraMessageTemplates, messageTemplateName } from "@/i18n/messageTemplates";
import { useTranslation } from 'react-i18next'

const LANGS = [["pt", "Português"], ["en", "English"], ["fr", "Français"], ["es", "Español"], ["de", "Deutsch"], ["it", "Italiano"], ["ru", "Русский"], ["zh", "中文"]];

export default function Messages() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const { t, i18n } = useTranslation();
  const m = messageText(i18n.language);
  const [lang, setLang] = useState(() => i18n.language?.split("-")[0] || "pt");
  useEffect(() => { setLang(i18n.language?.split("-")[0] || "pt"); }, [i18n.language]);
  const [templateId, setTemplateId] = useState("initial_presentation");
  const [contactId, setContactId] = useState("");
  const [propertyId, setPropertyId] = useState("");
  const [draft, setDraft] = useState("");
  const [whatsAppNumber, setWhatsAppNumber] = useState("");
  const [attachment, setAttachment] = useState(null);
  const [attachmentInputKey, setAttachmentInputKey] = useState(0);
  const [subject, setSubject] = useState("");
  const [recipient, setRecipient] = useState("");
  const [sending, setSending] = useState(false);
  const [openedMail, setOpenedMail] = useState(null);
  const [loadingMail, setLoadingMail] = useState(false);
  const inbox = useQuery({
    queryKey: ["investor-mail-inbox"],
    queryFn: async () => (await api.get("/investor-mail/inbox")).data.messages,
    enabled: isAdmin,
    retry: false,
  });
  const sent = useQuery({
    queryKey: ["investor-mail-sent"],
    queryFn: async () => (await api.get("/investor-mail/sent")).data.messages,
    enabled: isAdmin,
    retry: false,
  });


  const { data: templates = [] } = useQuery({ queryKey: ["templates"], queryFn: async () => (await api.get("/templates")).data });
  const { data: contacts = [] } = useQuery({ queryKey: ["contacts", {}], queryFn: async () => (await api.get("/contacts")).data });
  const { data: properties = [] } = useQuery({ queryKey: ["properties", {}], queryFn: async () => (await api.get("/properties")).data });
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: async () => (await api.get("/settings")).data });

  const tpl = templates.find((t) => t.id === templateId);
  const contact = contacts.find((c) => c.id === contactId);
  const property = properties.find((p) => p.id === propertyId);

  const generate = () => {
    if (!tpl) return;
    const extra = extraMessageTemplates[lang]?.[tpl.id];
    let body = extra?.[1] || tpl.body[lang] || tpl.body.en;
    const map = {
      contact: contact?.name || "[Contact]",
      property: property?.title || "[Property]",
      location: property ? t('cityDistrict', '{{city}}, {{district}}', { city: property.city, district: property.district }) : "[Location]",
      price: property ? price(property.asking_price) : "[Price]",
      consultant: settings?.consultant_name || "Consultant",
    };
    Object.entries(map).forEach(([k, v]) => { body = body.replaceAll(`{${k}}`, v); });
    setDraft(body);
    setSubject((extra?.[0] || tpl?.subject?.[lang] || tpl?.subject?.en || "").replaceAll("{property}", map.property));
    toast.success(m("draftReady"));
  };

  const copy = () => { navigator.clipboard.writeText(draft); toast.success(m("copied")); };
  const sendEmail = async () => {
    const to = (recipient || contact?.email || "").trim();
    if (!to) return toast.error(m("chooseRecipient"));
    if (!subject.trim() || !draft.trim()) return toast.error(m("required"));
    if (!window.confirm(m("confirmEmail", { to }))) return;
    setSending(true);
    try {
      if (attachment) {
        const form = new FormData();
        form.append("to", to);
        form.append("subject", subject.trim());
        form.append("body", draft.trim());
        form.append("file", attachment);
        await api.post("/investor-mail/send-with-attachment", form);
      } else {
        await api.post("/investor-mail/send", { to, subject: subject.trim(), body: draft.trim() });
      }
      setAttachment(null);
      setAttachmentInputKey((value) => value + 1);
      sent.refetch();
      toast.success(m("sentEmail", { to }));
    } catch (error) {
      toast.error(error.response?.data?.detail || m("sendError"));
    } finally {
      setSending(false);
    }
  };
  const openMail = async (id) => {
    setLoadingMail(true);
    try {
      setOpenedMail((await api.get(`/investor-mail/inbox/${encodeURIComponent(id)}`)).data);
    } catch (error) {
      toast.error(error.response?.data?.detail || m("openError"));
    } finally {
      setLoadingMail(false);
    }
  };
  const replyToMail = () => {
    if (!openedMail) return;
    const sender = openedMail.from.match(/<([^<>]+)>/)?.[1] || openedMail.from.trim();
    setRecipient(sender);
    setSubject(openedMail.subject.startsWith("Re:") ? openedMail.subject : `Re: ${openedMail.subject}`);
    setDraft(`\n\n--- ${m("original")} ---\n${openedMail.body}`);
    toast.success(m("replyReady"));
  };
  const deleteInboxMail = async (mail) => {
    if (!window.confirm(m("confirmInboxDelete", { subject: mail.subject || m("noSubject") }))) return;
    try {
      await api.delete(`/investor-mail/inbox/${encodeURIComponent(mail.id)}`);
      if (openedMail?.id === mail.id) setOpenedMail(null);
      await inbox.refetch();
      toast.success(m("inboxDeleted"));
    } catch (error) {
      toast.error(error.response?.data?.detail || m("inboxDeleteError"));
    }
  };
  const deleteSentMail = async (mail) => {
    if (!window.confirm(m("confirmSentDelete", { subject: mail.subject || m("noSubject") }))) return;
    try {
      await api.delete(`/investor-mail/sent/${encodeURIComponent(mail.id)}`);
      await sent.refetch();
      toast.success(m("sentDeleted"));
    } catch (error) {
      toast.error(error.response?.data?.detail || m("sentDeleteError"));
    }
  };
  const cancelDraft = () => {
    setSubject("");
    setDraft("");
    setAttachment(null);
    setAttachmentInputKey((value) => value + 1);
    toast.success(m("draftCancelled"));
  };

  const sendWhatsApp = () => {
    const entered = whatsAppNumber || contact?.whatsapp || contact?.mobile || contact?.phone || "";
    const number = entered.replace(/\D/g, "");
    if (number.length < 9 || number.length > 15) {
      return toast.error(m("invalidNumber"));
    }
    if (!draft.trim()) return toast.error(m("writeMessage"));
    window.open(`https://wa.me/${number}?text=${encodeURIComponent(draft)}`, "_blank", "noopener,noreferrer");
  };

  return (
    <div>
      <PageHeader title={m("title")} subtitle={m("subtitle")} />

      <div className="bg-blue-50 border border-blue-200 rounded-sm px-4 py-3 mb-6 flex items-start gap-2 text-sm text-blue-800">
        <Info size={18} className="flex-shrink-0 mt-0.5" />
        <span>{m("notice")}</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200 rounded-sm p-6 space-y-4">
          <div>
            <Label>{t('template', 'Template')}</Label>
            <Select value={templateId} onValueChange={setTemplateId}>
              <SelectTrigger data-testid="template-select" className="mt-1 rounded-sm bg-white"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white max-h-64">{templates.map((t) => <SelectItem key={t.id} value={t.id}>{messageTemplateName(t.id, i18n.language, t.name)}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>{t('language', 'Language')}</Label>
            <Select value={lang} onValueChange={setLang}>
              <SelectTrigger data-testid="lang-select" className="mt-1 rounded-sm bg-white"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">{LANGS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>{t('contact', 'Contact')}</Label>
            <Select value={contactId} onValueChange={(value) => { setContactId(value); setRecipient(""); }}>
              <SelectTrigger className="mt-1 rounded-sm bg-white"><SelectValue placeholder={t('selectContact', 'Select contact')} /></SelectTrigger>
              <SelectContent className="bg-white max-h-64">{contacts.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>{t('property', 'Property')}</Label>
            <Select value={propertyId} onValueChange={setPropertyId}>
              <SelectTrigger className="mt-1 rounded-sm bg-white"><SelectValue placeholder={t('selectProperty', 'Select property')} /></SelectTrigger>
              <SelectContent className="bg-white max-h-64">{properties.map((p) => <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Button data-testid="generate-draft" onClick={generate} className="w-full rounded-sm bg-[#0B2545] hover:bg-[#1a365d] text-white">{t('generateDraft', 'Generate Draft')}</Button>
        </div>

        <div className="bg-white border border-slate-200 rounded-sm p-6 space-y-4">
          <Label>{m("to")}</Label>
          <input type="email" value={recipient || contact?.email || ""} onChange={(e) => setRecipient(e.target.value)} placeholder={m("toPlaceholder")} className="w-full border border-slate-200 rounded-sm p-2" />
          <Label>{m("attachment")}</Label>
          <input key={attachmentInputKey} type="file" accept=".pdf,.doc,.docx,.png,.jpg,.jpeg" className="w-full text-sm" onChange={(event) => {
            const selected = event.target.files?.[0] || null;
            if (selected && selected.size > 10 * 1024 * 1024) {
              toast.error(m("attachmentSize"));
              event.target.value = "";
              setAttachment(null);
              return;
            }
            setAttachment(selected);
          }} />
          {attachment && <span className="text-xs text-slate-600">{attachment.name}</span>}
          <Label>{m("whatsappNumber")}</Label>
          <input
            type="tel"
            value={whatsAppNumber || contact?.whatsapp || contact?.mobile || contact?.phone || ""}
            onChange={(event) => setWhatsAppNumber(event.target.value)}
            placeholder="+351 XXX XXX XXX"
            className="w-full border border-slate-200 rounded-sm p-2"
          />
          <Label>{m("subject")}</Label>
          <input value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={250} className="w-full border border-slate-200 rounded-sm p-2" />
          <Label>{t('draftEditable', 'Draft (editable)')}</Label>
          <Textarea data-testid="draft-body" value={draft} onChange={(e) => setDraft(e.target.value)} rows={14} className="rounded-sm font-mono text-sm" placeholder={t('yourGeneratedDraftWillAppearHere', 'Your generated draft will appear here…')} />

            <div
              className="border border-slate-200 rounded-sm bg-white p-4"
              data-testid="email-signature-preview"
            >
              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="sm:pr-5 sm:border-r-2 sm:border-[#b18a3b] shrink-0">
                  <img
                    src="/panatlantik-logo.png"
                    alt="Panatlantik International"
                    className="w-[105px] h-auto object-contain"
                  />
                </div>

                <div className="text-sm leading-relaxed">
                  <div className="font-bold text-black text-base">
                    Victor Senra
                  </div>

                  <div className="text-xs font-bold tracking-wide text-[#C6A15B] mt-1.5">
                    PANATLANTIK INTERNATIONAL
                  </div>

                  <div className="text-xs text-slate-700 mt-1">
                    International Real Estate &amp; Investment Advisory
                  </div>

                  <div className="mt-3 text-xs text-slate-800 space-y-1.5">
                    <a href="tel:+351934158151" className="flex items-center gap-2 hover:underline">
                      <Phone size={13} weight="regular" className="text-slate-700 shrink-0" />
                      <span>+351 934 158 151</span>
                    </a>

                    <a href="https://wa.me/351913814664" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-emerald-600 hover:underline" data-testid="signature-business-whatsapp">
                      <WhatsappLogo size={13} weight="regular" className="shrink-0" aria-hidden="true" />
                      <span>WhatsApp Business: +351 913 814 664</span>
                    </a>

                    <a href="mailto:victor@panatlantik.com" className="flex items-center gap-2 hover:underline">
                      <EnvelopeSimple size={13} weight="regular" className="text-slate-700 shrink-0" />
                      <span>victor@panatlantik.com</span>
                    </a>

                    <a href="https://www.panatlantik.com" target="_blank" rel="noreferrer" className="flex items-center gap-2 hover:underline">
                      <GlobeSimple size={13} weight="regular" className="text-slate-700 shrink-0" />
                      <span>www.panatlantik.com</span>
                    </a>
                  </div>
                </div>
              </div>

              <div className="border-t border-slate-200 mt-3 pt-2 text-[10px] leading-relaxed text-slate-500">
                <strong>Confidentiality Notice:</strong>{" "}
                This email and any attachments are confidential and intended solely for the recipient. Unauthorized use or disclosure is prohibited.
              </div>
            </div>
          <div className="flex flex-wrap gap-2">
            <Button onClick={copy} variant="outline" className="rounded-sm" data-testid="copy-draft"><Copy size={16} className="mr-1.5" /> {t('copy', 'Copy')}</Button>
            <Button
                type="button"
                onClick={cancelDraft}
                variant="outline"
                className="rounded-sm"
                data-testid="cancel-draft"
              >
                {m("cancelDraft")}
              </Button>
              <Button onClick={sendEmail} disabled={sending} variant="outline" className="rounded-sm" data-testid="send-investor-email"><EnvelopeSimple size={16} className="mr-1.5" /> {sending ? m("sending") : m("sendEmail")}</Button>
            <Button onClick={sendWhatsApp} variant="outline" className="rounded-sm text-emerald-600"><WhatsappLogo size={16} className="mr-1.5" /> {m("openWhatsApp")}</Button>
          </div>
        </div>
      </div>

      {isAdmin && (
        <>
<section className="bg-white border border-slate-200 rounded-sm p-6 mt-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif text-lg">{m("investorInbox")}</h3>
          <Button variant="outline" onClick={() => inbox.refetch()}>{m("refresh")}</Button>
        </div>
        {inbox.isLoading && <p>{m("loadingInbox")}</p>}
        {inbox.isError && <p className="text-red-700">{m("inboxError")}</p>}
        {!inbox.isError && inbox.data?.length === 0 && <p>{m("noMessages")}</p>}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {(inbox.data || []).map((mail) => (
              <div key={mail.id} className="border rounded-sm p-3">
                <button type="button" onClick={() => openMail(mail.id)} className="block w-full text-left hover:bg-slate-50">
                  <strong className="block">{mail.subject || m("noSubject")}</strong>
                  <span className="block text-sm text-slate-600 break-all">{mail.from}</span>
                  <span className="block text-xs text-slate-500">{mail.date}</span>
                </button>
                <Button type="button" variant="outline" className="mt-2 text-red-700" onClick={() => deleteInboxMail(mail)}>{m("delete")}</Button>
              </div>
            ))}
          </div>
          <div className="border rounded-sm p-4 whitespace-pre-wrap break-words">
            {loadingMail ? m("loadingMessage") : openedMail ? (
              <>
                <strong>{openedMail.subject || m("noSubject")}</strong>
                <div className="text-sm text-slate-600 mt-2">{m("from")}: {openedMail.from}</div>
                <div className="text-sm text-slate-500">{openedMail.date}</div>
                <div className="mt-4 text-sm">{openedMail.body}</div>
                <Button variant="outline" onClick={replyToMail} className="mt-4">{m("prepareReply")}</Button>
              </>
            ) : m("selectMessage")}
          </div>
        </div>
      </section>
      <section className="bg-white border border-slate-200 rounded-sm p-6 mt-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif text-lg">{m("sentHistory")}</h3>
          <Button variant="outline" onClick={() => sent.refetch()}>{m("refresh")}</Button>
        </div>
        {sent.isError && <p className="text-red-700">{m("sentError")}</p>}
        {!sent.isError && sent.data?.length === 0 && <p>{m("noSent")}</p>}
        <div className="space-y-3 max-h-96 overflow-y-auto">
          {(sent.data || []).map((mail) => (
            <details key={mail.id} className="border rounded-sm p-3">
              <summary className="cursor-pointer"><strong>{mail.subject}</strong> · {mail.to} · {mail.sent_at}</summary>
              <p className="whitespace-pre-wrap break-words mt-3">{mail.body}</p>
              {mail.attachment_name && <p className="text-sm text-slate-600 mt-2">{m("attached")}: {mail.attachment_name}</p>}
              <Button type="button" variant="outline" className="mt-3 text-red-700" onClick={() => deleteSentMail(mail)}>{m("deleteHistory")}</Button>
            </details>
          ))}
        </div>
      </section>
      <WhatsAppInbox contacts={contacts} selectedContact={contact} />
        </>
      )}
    </div>
  );
}
